import React, {
  ForwardedRef,
  useImperativeHandle,
  useState,
} from 'react';
import { styled } from 'dripsy';
import { View } from 'react-native';

import { useAsyncInit, usePopover } from '@hooks';
import { IconTouchable } from '../../common/IconTouchable';
import {
  IPopoverProps,
  Popover,
  PopoverPositionEnum,
} from '../Popover';
import { EmojiItem, IEmoji } from './EmojiItem';
import { ListView } from '../ListView';

const ListContainer = styled(View)({
  size: 220,
  borderRadius: 12,
});

export type EmojiPickerRefType = {
  toggle: () => void;
};

export interface IEmojiPickerProps
  extends Omit<IPopoverProps, 'children'> {
  onSelect(value: string): void;
  withIcon?: boolean;
  pickerRef?: ForwardedRef<EmojiPickerRefType>;
}

export function EmojiPicker({
  onSelect,
  withIcon = true,
  pickerRef,
  offset = 10,
  ...props
}: IEmojiPickerProps) {
  const [data, setData] = useState<IEmoji[] | null>(null);
  const { ref: innerRef, togglePopover } = usePopover();

  useAsyncInit(async () => {
    const res = await import('./emojis.json');
    setData(res.data);
  });

  useImperativeHandle(pickerRef, () => ({
    toggle: () => innerRef.current?.togglePopover(),
  }));

  function pickEmoji(item: IEmoji) {
    onSelect(item.symbol);
    togglePopover();
  }

  return (
    <Popover
      ref={innerRef}
      position={PopoverPositionEnum.TopLeft}
      offset={offset}
      {...props}
    >
      {!withIcon ? (
        <View />
      ) : (
        <IconTouchable
          name="smile"
          size={24}
          onPress={togglePopover}
        />
      )}

      <ListContainer>
        <ListView<IEmoji>
          numColumns={6}
          data={data}
          isLoading={!data}
          keyExtractor={(item) => item.id}
          renderItem={({ item }) => (
            <EmojiItem item={item} onPress={pickEmoji} />
          )}
        />
      </ListContainer>
    </Popover>
  );
}
